// frontend/components/ui/RegeneratePdf.tsx

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Modal, Box, Button, Typography, Alert, LinearProgress } from '@mui/material';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useWebSocketContext  from '../../components/hooks/useWebSocketContext';

interface RegeneratePdfProps {
  open: boolean;
  onClose: () => void;
  filename: string;
  baseUrl: string;
  licenseKey: string;
}

const RegeneratePdf: React.FC<RegeneratePdfProps> = ({
  open,
  onClose,
  filename,
  baseUrl,
  licenseKey,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversionStatus, setConversionStatus] = useState<string>('');
  const [isViewerReady, setIsViewerReady] = useState(false);
  const [viewerRefSet, setViewerRefSet] = useState(false);
  const instanceRef = useRef<WebViewerInstance | null>(null);
  const viewerDivRef = useRef<HTMLDivElement | null>(null);
  const initializationRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  const { sendBinaryToWs } = useWebSocketContext();
  const { session_id, user_id } = useSelector((state: RootState) => state.session);
  const activeChatGroup = useSelector((state: RootState) => state.chatHistory.activeChatGroup);

  const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95vw',
    height: '95vh',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 2,
    display: 'flex',
    flexDirection: 'column' as 'column',
  };

  // Enhanced WebViewer container styles
  const webViewerContainerStyle = {
    height: '80vh',  // Fixed height
    width: '100%',
    minHeight: '600px', // Ensure minimum height
    border: '1px solid #ccc',
    borderRadius: '4px',
    overflow: 'hidden', // Prevent scrollbars
    position: 'relative' as 'relative',
    // Always keep the div in DOM but control visibility with opacity and z-index
    opacity: isViewerReady ? 1 : 0,
    pointerEvents: isViewerReady ? 'auto' : 'none',
    transition: 'opacity 0.3s ease-in-out',
    backgroundColor: '#f5f5f5', // Show a background to indicate the container is there
  };

  const sendMetadataAndBinaryData = (metadata: string, binaryData: Blob) => {
    const delimiter = '\0';
    const combinedMessage = new Blob([metadata, delimiter, binaryData]);
    sendBinaryToWs(combinedMessage);
  };

  // Cleanup function to properly dispose WebViewer
  const cleanupWebViewer = useCallback(() => {
    console.log('Cleaning up WebViewer...');
    if (instanceRef.current) {
      try {
        instanceRef.current.UI.dispose();
        instanceRef.current = null;
      } catch (disposeError) {
        console.warn('Error disposing WebViewer:', disposeError);
      }
    }
    initializationRef.current = false;
    setIsViewerReady(false);
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setError(null);
      setIsLoading(false);
      setConversionStatus('');
      setIsViewerReady(false);
      mountedRef.current = true;
      console.log('Modal opened, resetting state');
      
      // Debug environment variables immediately when modal opens
      console.log('🔧 Environment Variables Check:', {
        VITE_FILES_BASE_URL: import.meta.env.VITE_FILES_BASE_URL,
        VITE_APRYSE_LICENSE_KEY: import.meta.env.VITE_APRYSE_LICENSE_KEY,
        baseUrlProp: baseUrl,
        licenseKeyProp: licenseKey,
        allEnvVars: import.meta.env
      });
    } else {
      console.log('Modal closing, cleaning up...');
      cleanupWebViewer();
      mountedRef.current = false;
    }
  }, [open, cleanupWebViewer]);

  // Check if DOCX exists
  const checkDocxExists = useMemo(() => {
    return async (docxFilename: string): Promise<boolean> => {
      try {
        console.log(`Checking if DOCX exists: ${docxFilename}`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        
        // Use HEAD request instead of GET with Range header to avoid 206 responses
        const response = await fetch(`${baseUrl}open/${docxFilename}`, { 
          method: 'HEAD',
          headers: {
            'Cache-Control': 'no-cache',
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        console.log(`Document check response: ${response.status} ${response.statusText}`);
        
        const exists = response.ok && response.status === 200;
        console.log(`DOCX file ${docxFilename} exists: ${exists}`);
        return exists;
      } catch (error) {
        console.warn(`Error checking DOCX existence: ${error}`);
        return false;
      }
    };
  }, [baseUrl]);

  // Convert PDF to DOCX
  const convertPdfToDocx = useMemo(() => {
    return async (pdfFilename: string): Promise<string> => {
      try {
        console.log(`Starting PDF to DOCX conversion for: ${pdfFilename}`);
        setConversionStatus('Preparing document for editing...');
        
        const response = await fetch(`${baseUrl}convert-pdf-to-docx/${pdfFilename}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorMessage;
          } catch { /* ignore */ }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        console.log('Conversion result:', result);
        
        if (result.status === 'success' && result.docx_file_name) {
          setConversionStatus('Document prepared successfully!');
          return result.docx_file_name;
        } else {
          throw new Error('Conversion failed - no DOCX file returned');
        }
      } catch (error) {
        console.error('PDF to DOCX conversion failed:', error);
        throw error;
      }
    };
  }, [baseUrl]);

  // Main WebViewer initialization
  useEffect(() => {
    // Debug logging for environment variables and state
    console.log('🔍 WebViewer useEffect triggered with:', {
      open,
      viewerDivRef: !!viewerDivRef.current,
      initializationRef: initializationRef.current,
      filename,
      baseUrl,
      licenseKey,
      mountedRef: mountedRef.current,
      viewerRefSet
    });

    const initializeViewer = async () => {
      console.log('🎯 InitializeViewer called - checking conditions...');
      console.log('Conditions check:', {
        open,
        viewerDivRef: !!viewerDivRef.current,
        initializationRef: initializationRef.current,
        shouldProceed: !(!open || !viewerDivRef.current || initializationRef.current)
      });

      if (!open || !viewerDivRef.current || initializationRef.current) {
        console.log('❌ Initialization conditions not met, exiting early');
        return;
      }

      // Check if environment variables are defined
      if (!baseUrl) {
        console.error('❌ baseUrl is undefined! Environment variable VITE_FILES_BASE_URL not set');
        setError('Configuration error: Base URL not configured. Please check environment variables.');
        return;
      }

      if (!licenseKey) {
        console.warn('⚠️ licenseKey is undefined! Environment variable VITE_APRYSE_LICENSE_KEY not set');
      }

      initializationRef.current = true;
      console.log('🚀 Starting WebViewer initialization...');
      setIsLoading(true);
      setError(null);
      setConversionStatus('');
      setIsViewerReady(false);
      
      try {
        const docxFilename = filename.replace('.pdf', '.docx');
        console.log(`📄 Original PDF: ${filename}, Target DOCX: ${docxFilename}`);
        
        // Check if DOCX file exists
        let docxExists = false;
        let finalDocxFilename = docxFilename;
        
        docxExists = await checkDocxExists(docxFilename);
        
        // Convert if needed
        if (!docxExists) {
          console.log('📝 DOCX not found, attempting conversion...');
          try {
            finalDocxFilename = await convertPdfToDocx(filename);
            await new Promise(resolve => setTimeout(resolve, 1500));
            docxExists = await checkDocxExists(finalDocxFilename);
            if (!docxExists) {
              throw new Error('Document preparation failed - file could not be verified');
            }
          } catch (conversionError) {
            console.error('❌ Conversion failed:', conversionError);
            if (mountedRef.current) {
              setError(`Cannot load document for editing: ${conversionError instanceof Error ? conversionError.message : String(conversionError)}`);
              setIsLoading(false);
            }
            initializationRef.current = false;
            return;
          }
        }

        if (!mountedRef.current || !open) {
          console.log('⚠️ Component unmounted during initialization');
          initializationRef.current = false;
          return;
        }

        console.log(`🔄 Loading document in WebViewer: ${finalDocxFilename}`);
        setConversionStatus('Setting up the editor environment...');

        // Wait a moment for DOM to be ready
        await new Promise(resolve => setTimeout(resolve, 200));

        const documentUrl = `${baseUrl}open/${finalDocxFilename}`;
        console.log('🌐 Document URL:', documentUrl);

        // Initialize WebViewer with enhanced configuration
        const webViewerInstance = await WebViewer(
          {
            path: '/webviewer',
            licenseKey: licenseKey || '', // Handle missing license gracefully
            disabledElements: [
              'searchButton', 
              'panToolButton',
              'leftPanelButton',
              'toggleNotesButton'
            ],
            enableOfficeEditing: true,
            fullAPI: true,
            enableRedaction: false,
            enableMeasurement: false,
            css: `
              .HeaderItems {
                background-color: #f5f5f5;
              }
              .DocumentContainer {
                background-color: #ffffff;
              }
            `,
          },
          viewerDivRef.current
        );

        if (!mountedRef.current) {
          console.log('⚠️ Component unmounted after WebViewer creation');
          webViewerInstance.UI.dispose();
          initializationRef.current = false;
          return;
        }

        console.log('✅ WebViewer instance created successfully');
        instanceRef.current = webViewerInstance;
        const { Core, UI } = webViewerInstance;

        // Set up enhanced document handlers
        let documentLoadTimeout: NodeJS.Timeout;
        let documentLoadAttempts = 0;
        const maxLoadAttempts = 2;
        
        const handleDocumentLoaded = () => {
          console.log('📋 Document loaded successfully in WebViewer');
          if (documentLoadTimeout) clearTimeout(documentLoadTimeout);
          if (mountedRef.current) {
            setIsLoading(false);
            setConversionStatus('');
            setIsViewerReady(true);
            console.log('✅ WebViewer is ready for use');
            
            // Fit the document to page width after a short delay
            setTimeout(() => {
              if (instanceRef.current && mountedRef.current) {
                try {
                  const { Core: coreRef } = instanceRef.current;
                  coreRef.documentViewer.setFitMode(coreRef.documentViewer.FitMode.FitWidth);
                  console.log('📏 Document fit to width');
                } catch (fitError) {
                  console.warn('⚠️ Could not fit document to width:', fitError);
                }
              }
            }, 1000);
          }
        };

        const handleDocumentError = (error: any) => {
          console.error('📋 Document loading error:', error);
          if (documentLoadTimeout) clearTimeout(documentLoadTimeout);
          
          // Try alternative loading method if this is the first attempt
          if (documentLoadAttempts < maxLoadAttempts && mountedRef.current) {
            documentLoadAttempts++;
            console.log(`🔄 Attempting alternative document loading method (attempt ${documentLoadAttempts}/${maxLoadAttempts})`);
            
            setTimeout(() => {
              if (mountedRef.current && instanceRef.current) {
                tryLoadDocument();
              }
            }, 2000);
            return;
          }
          
          if (mountedRef.current) {
            const errorMessage = error?.message || error?.toString() || 'Unknown document loading error';
            setError(`Document loading failed: ${errorMessage}`);
            setIsLoading(false);
            setConversionStatus('');
            setIsViewerReady(false);
          }
        };

        const tryLoadDocument = async () => {
          try {
            setConversionStatus('Loading document...');
            
            // Set a timeout for document loading
            documentLoadTimeout = setTimeout(() => {
              if (mountedRef.current && !isViewerReady) {
                console.warn('⏰ Document loading timeout reached');
                if (documentLoadAttempts < maxLoadAttempts) {
                  handleDocumentError(new Error('Loading timeout - trying alternative method'));
                } else {
                  setError('Document loading timed out. Please try again.');
                  setIsLoading(false);
                  setConversionStatus('');
                }
              }
            }, 15000); // 15 second timeout

            console.log(`📂 Loading document into WebViewer (attempt ${documentLoadAttempts})...`);
            
            if (documentLoadAttempts === 1) {
              // First attempt: Use loadDocument method
              await UI.loadDocument(documentUrl, {
                filename: finalDocxFilename,
                enableOfficeEditing: true,
              });
            } else {
              // Second attempt: Use alternative loading method
              console.log('🔄 Using alternative document loading method');
              await Core.documentViewer.loadDocument(documentUrl, {
                enableOfficeEditing: true,
              });
            }
            
            console.log('📂 Document load request sent successfully');
          } catch (loadError) {
            console.error('📂 Error sending document load request:', loadError);
            if (documentLoadAttempts < maxLoadAttempts) {
              handleDocumentError(loadError);
            } else {
              throw new Error(`All document loading attempts failed: ${loadError instanceof Error ? loadError.message : String(loadError)}`);
            }
          }
        };

        // Set up document event listeners with error handling
        try {
          Core.documentViewer.addEventListener('documentLoaded', handleDocumentLoaded);
          Core.documentViewer.addEventListener('documentLoadedError', handleDocumentError);
          
          // Start the first loading attempt
          documentLoadAttempts++;
          await tryLoadDocument();

        } catch (setupError) {
          console.error('🔧 Error setting up document handlers:', setupError);
          if (mountedRef.current) {
            setError(`Failed to set up document viewer: ${setupError instanceof Error ? setupError.message : String(setupError)}`);
            setIsLoading(false);
            setConversionStatus('');
          }
          initializationRef.current = false;
          return;
        }

        console.log('🎯 WebViewer initialization completed');

      } catch (error) {
        console.error('💥 Error during viewer initialization:', error);
        if (mountedRef.current) {
          // Don't show the full error stack to users, just a friendly message
          const userFriendlyMessage = error instanceof Error 
            ? (error.message.includes('WebViewer') 
                ? 'Failed to initialize document viewer. Please refresh the page and try again.'
                : error.message)
            : 'An unexpected error occurred while loading the document viewer.';
            
          setError(userFriendlyMessage);
          setIsLoading(false);
          setConversionStatus('');
          setIsViewerReady(false);
        }
        initializationRef.current = false;
      }
    };

    // Add a small delay to ensure DOM is ready before checking ref
    const checkAndInitialize = () => {
      console.log('🔄 Checking ref availability:', { 
        open, 
        hasRef: !!viewerDivRef.current,
        refElement: viewerDivRef.current?.tagName 
      });
      
      if (open && viewerDivRef.current) {
        console.log('🎬 Scheduling WebViewer initialization...');
        const initTimeout = setTimeout(initializeViewer, 150);
        return () => clearTimeout(initTimeout);
      } else {
        console.log('⏸️ Not scheduling initialization:', { open, viewerDivExists: !!viewerDivRef.current });
        
        // If modal is open but ref isn't ready, try again after a short delay
        if (open && !viewerDivRef.current) {
          console.log('🔄 Modal open but ref not ready, retrying in 100ms...');
          const retryTimeout = setTimeout(checkAndInitialize, 100);
          return () => clearTimeout(retryTimeout);
        }
      }
    };

    return checkAndInitialize();
  }, [open, filename, baseUrl, licenseKey, viewerRefSet]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      cleanupWebViewer();
    };
  }, [cleanupWebViewer]);

  const handleRegenerate = async () => {
    try {
      if (!instanceRef.current) {
        throw new Error('WebViewer instance not available');
      }

      console.log('🔄 Starting document regeneration...');
      setIsLoading(true);
      const { Core } = instanceRef.current;

      const doc = Core.documentViewer.getDocument();
      if (!doc) {
        throw new Error('No document loaded in WebViewer');
      }

      console.log('📤 Exporting document with changes...');
      const xfdfString = await Core.annotationManager.exportAnnotations();
      const options = { xfdfString, extension: 'docx' };
      const data = await doc.getFileData(options);

      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      const docxFilename = filename.replace('.pdf', '.docx');
      const params: any = {
        assistant: 'keai',
        session_id: session_id,
        user_id: user_id,
        doc_name: docxFilename,
      };
      
      // Include chat_group_id if an active chat group exists
      if (activeChatGroup) {
        params.chat_group_id = activeChatGroup;
      }
      
      const metadata = JSON.stringify({
        method: 'regenerate_doc',
        params: params,
      });

      console.log('📡 Sending document for regeneration...');
      sendMetadataAndBinaryData(metadata, blob);
      console.log('✅ Document sent for regeneration');

      onClose();
    } catch (error) {
      console.error('❌ Regeneration error:', error);
      setError(`Failed to regenerate document: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    console.log('🔄 Retrying initialization...');
    setError(null);
    cleanupWebViewer();
    // Re-initialization will happen automatically via useEffect
  };

  // Callback ref to track when the div is attached
  const setViewerDivRef = useCallback((node: HTMLDivElement | null) => {
    console.log('🎯 ViewerDiv ref being set:', !!node);
    viewerDivRef.current = node;
    setViewerRefSet(!!node);
  }, []);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {error ? (
          <Box sx={{ padding: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Document Loading Error
              </Typography>
              {error}
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={onClose}>
                Close
              </Button>
              <Button variant="contained" onClick={handleRetry}>
                Retry
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {(isLoading || conversionStatus) && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '70vh',
                gap: 3
              }}>
                <Typography variant="h5" align="center">
                  {conversionStatus || 'Loading document editor...'}
                </Typography>
                <LinearProgress sx={{ width: '60%', height: 8, borderRadius: 4 }} />
                <Typography variant="body2" color="text.secondary" align="center">
                  {conversionStatus ? 'Please wait while we prepare your document...' : 'Initializing WebViewer...'}
                </Typography>
              </Box>
            )}
            
            {/* WebViewer Container */}
            <Box 
              ref={setViewerDivRef} 
              sx={webViewerContainerStyle}
            />
            
            {/* Control Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              marginTop: 2,
              gap: 1,
              flexShrink: 0
            }}>
              <Button 
                variant="outlined" 
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleRegenerate}
                disabled={isLoading || !!error || !!conversionStatus || !isViewerReady}
              >
                {isLoading ? 'Processing...' : 'Regenerate Document'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default RegeneratePdf;